import './lib/gtag'; // Initialize GA4 globally on every page
import { createRoot } from "react-dom/client";
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from './components/ErrorBoundary';
import App from "./App.tsx";
import "./index.css";
import "./styles/qr-payment.css";
import { logAppError } from './utils/logAppError';

// Stale dynamic-import detector — fires after a deploy when an old tab tries
// to load a hashed chunk that no longer exists on the CDN.
function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg = err instanceof Error ? err.message : String(err);
  return /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|error loading dynamically imported module/i.test(msg);
}

// One-shot guarded reload to recover from stale chunks without infinite loops.
function tryChunkReload(): boolean {
  try {
    const last = Number(localStorage.getItem('chunk_reload_at') || '0');
    if (Date.now() - last < 60_000) return false;
    localStorage.setItem('chunk_reload_at', String(Date.now()));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

// Detect URLs we never want to log/treat as user-facing crashes.
function isGarbledRoute(): boolean {
  try {
    const path = window.location.pathname || '';
    return /[^A-Za-z0-9/_\-.~%]/.test(decodeURIComponent(path));
  } catch {
    return false;
  }
}

// Errors we never want to record as crashes (expected lifecycle events).
function isIgnorableError(err: unknown): boolean {
  if (!err) return true;
  const msg = err instanceof Error ? err.message : String(err);
  const code = (err as any)?.code;
  if (code === 'session_expired' || code === 'refresh_token_not_found') return true;
  if (/Invalid Refresh Token|Session Expired|Auth session missing/i.test(msg)) return true;
  // ResizeObserver loop noise — Chrome bug, benign.
  if (/ResizeObserver loop/i.test(msg)) return true;
  // Provider-not-mounted errors are HMR or top-level boot races; not user-impacting.
  if (/must be used within .*Provider/i.test(msg)) return true;
  // Tombstone service-worker registration failures from old deploys.
  if (/Failed to register a ServiceWorker/i.test(msg)) return true;
  // WebGL/maplibre constructor failures — handled in-component with a fallback UI.
  if (/webglcontextcreationerror|Failed to initialize WebGL|WebGL.*not supported/i.test(msg)) return true;
  // AbortError noise from supabase-js / fetch when components unmount mid-request.
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  if (/AbortError|signal is aborted without reason|The operation was aborted/i.test(msg)) return true;
  // Stale dynamic-import chunks — handled by an auto-reload below.
  if (isChunkLoadError(err)) return true;
  return false;
}

// FIX: Global error handlers to capture crashes before React mounts
const saveCrashReport = (error: Error | string, source: string) => {
  try {
    const report = {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      source,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };
    localStorage.setItem('crash_report', JSON.stringify(report));
  } catch {
    // Ignore localStorage errors
  }
};

window.addEventListener('error', (event) => {
  const err = event.error || event.message;
  if (isChunkLoadError(err)) {
    if (tryChunkReload()) return;
  }
  if (isIgnorableError(err)) return;
  saveCrashReport(err, 'window.onerror');
  void logAppError({
    source: 'client:window.onerror',
    message: err instanceof Error ? err.message : String(err),
    severity: 'error',
    metadata: { stack: err instanceof Error ? err.stack : undefined },
  });
});

window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event.reason)) {
    if (tryChunkReload()) return;
  }
  if (isIgnorableError(event.reason)) return;
  saveCrashReport(event.reason, 'unhandledrejection');
  const reason = event.reason;
  let message: string;
  let serializedReason: string | undefined;
  if (reason instanceof Error) {
    message = reason.message;
  } else {
    try {
      serializedReason = JSON.stringify(reason, Object.getOwnPropertyNames(reason ?? {})).slice(0, 2000);
    } catch {
      serializedReason = String(reason);
    }
    message = serializedReason || String(reason);
  }
  // Garbled / emoji-laced URLs (e.g. /exam✅) — downgrade to info noise.
  const garbled = isGarbledRoute();
  void logAppError({
    source: 'client:unhandledrejection',
    message: garbled ? `[garbled_url] ${message}` : message,
    severity: garbled ? 'info' : 'error',
    metadata: {
      stack: reason instanceof Error ? reason.stack : undefined,
      serializedReason,
      reasonType: reason?.constructor?.name ?? typeof reason,
      route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      promiseCtor: (event as any)?.promise?.constructor?.name,
      garbledUrl: garbled || undefined,
    },
  });
});

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </ErrorBoundary>
);

// One-time cleanup: a previous deploy registered /sw.js. Unregister any
// lingering service workers so users stop getting stale assets and CSP.
try {
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    const cleaned = localStorage.getItem('sw_cleaned_v2');
    if (!cleaned) {
      navigator.serviceWorker.getRegistrations()
        .then(async (regs) => {
          const results = await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
          return results.some(Boolean);
        })
        .then(async (didUnregister) => {
          try {
            const keys = (await caches?.keys?.()) ?? [];
            await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
          } catch { /* ignore */ }
          return didUnregister;
        })
        .then((didUnregister) => {
          try { localStorage.setItem('sw_cleaned_v2', String(Date.now())); } catch {}
          // If we actually killed a stale SW, force one reload so the
          // current page swaps to fresh assets immediately.
          if (didUnregister) {
            try { window.location.reload(); } catch { /* ignore */ }
          }
        })
        .catch(() => {
          try { localStorage.setItem('sw_cleaned_v2', String(Date.now())); } catch {}
        });
    }
  }
} catch { /* noop */ }
